import { type FC, memo, useEffect, useMemo, useRef, useState } from "react";

import { languageSelector } from "@/src/contexts";
import { useViewportInfoContext } from "@/src/contexts";

import type { ICashbackBrandTile } from "../CashbackCommonInterfaces";
import CashbackBrandsByCategoryItem from "./CashbackBrandsByCategoryItem";
import CashbackDropDownList, { type FilterDropDownListItem } from "./CashbackDropDownList";
import { CashbackMainPageResources } from "./CashbackMainPage.Resources";
import { CashbackMainPageResourcesEn } from "./CashbackMainPage.Resources.En";

import * as css from "./CashbackBrandsByCategory.module.css";

export interface ICashbackCategory {
    code: string;
    id: string;
}

export interface ICashbackBrandsByCategory {
    brandsCashback: ICashbackBrandTile[];
    activeCategoryId: string;
}

const paginationSize = 16;
const filterKey = { Popular: "popularity", Alphabetic: "alphabetic" };

const CashbackBrandsByCategory: FC<ICashbackBrandsByCategory> = memo((props) => {
    const [pagIndex, setPageIndex] = useState<number>(1);
    const [orderType, setOrderType] = useState<string>(filterKey.Popular);
    const observer = useRef<IntersectionObserver>();
    const { isMobileOrTablet } = useViewportInfoContext();
    const resources = languageSelector(
        CashbackMainPageResources.brandsCashbackSection,
        CashbackMainPageResourcesEn.brandsCashbackSection,
    );

    const dropdownFilterList: FilterDropDownListItem[] = [
        { key: filterKey.Alphabetic, value: resources.filterOptionAlphabetic },
        { key: filterKey.Popular, value: resources.filterOptionPopular },
    ];

    useEffect(() => {
        setPageIndex(1);
    }, [props.activeCategoryId]);

    const lastFromList = (node: HTMLDivElement) => {
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                if (props.brandsCashback.length > paginationSize * pagIndex) {
                    setPageIndex((previousIndex) => previousIndex + 1);
                }
            }
        });
        if (node) observer.current.observe(node);
    };

    const sortedList = useMemo(() => {
        const brandsList: ICashbackBrandTile[] = [...props.brandsCashback];

        if (orderType === filterKey.Alphabetic) {
            brandsList.sort((a, b) => (a.description || "").localeCompare(b.description || ""));
        }

        return brandsList;
    }, [props.brandsCashback?.length, orderType]);

    const handleChange = (order: string) => {
        if (order && order !== orderType) {
            setOrderType(order);
        }
    };

    const renderCashbackBrands = () => {
        const filterList = sortedList.slice(0, paginationSize * pagIndex);

        if (filterList?.length > 0) {
            return (
                <div className={css.sectionCashbackBrands} role="list">
                    {filterList.map((item: ICashbackBrandTile) => (
                        <CashbackBrandsByCategoryItem
                            key={item.id}
                            brand={item}
                            isMobile={isMobileOrTablet}
                        />
                    ))}
                    <div ref={lastFromList} />
                </div>
            );
        }

        return <div className={css.emptyList}>{resources.noBrandsText}</div>;
    };

    return (
        <div className={css.cashbackBrandsByCategoryComponent}>
            <div className={css.filter}>
                <span>{resources.filterText}</span>
                <CashbackDropDownList
                    optionList={dropdownFilterList}
                    optionStart={orderType}
                    onChange={handleChange}
                />
                <span>({props.brandsCashback.length > 500 ? "500+" : props.brandsCashback.length})</span>
            </div>
            {renderCashbackBrands()}
        </div>
    );
});

export default CashbackBrandsByCategory;
